import type { ReactElement } from 'react'
import { useState } from 'react'
import SectionHeader from '../components/SectionHeader'
import { useI18n, useT } from '../i18n/useI18n'
import { upcomingProducts } from '../data/upcomingProducts'
import { company } from '../data/company'

const STORAGE_KEY = 'sm-dev-unlocked'

// SHA-256 hash of the current preview passcode.
// Current passcode: "structura-shell-2026"
// To rotate:  echo -n "your-new-code" | sha256sum  then paste the hex here and rebuild.
const PASSCODE_HASH = '996d80061106871e8bc2192fc92452fafcb41f0d8a21399fac0a1ce0a6dcd5f3'

async function sha256(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input)
  const hash = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function isInitiallyUnlocked(): boolean {
  if (typeof window === 'undefined') return false
  return window.sessionStorage.getItem(STORAGE_KEY) === '1'
}

export default function Developers(): ReactElement {
  const t = useT()
  const { lang } = useI18n()
  const [unlocked, setUnlocked] = useState(isInitiallyUnlocked)
  const [code, setCode] = useState('')
  const [error, setError] = useState(false)
  const [checking, setChecking] = useState(false)

  const tryUnlock = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setChecking(true)
    setError(false)
    const hashed = await sha256(code.trim())
    if (hashed === PASSCODE_HASH) {
      sessionStorage.setItem(STORAGE_KEY, '1')
      setUnlocked(true)
    } else {
      setError(true)
    }
    setChecking(false)
  }

  if (!unlocked) {
    return (
      <section className="section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="feature-box">
                <div className="feature-icon">
                  <i className="bi bi-shield-lock"></i>
                </div>
                <h3>{t('developers.gate.heading')}</h3>
                <p className="description">{t('developers.gate.body')}</p>
                <form onSubmit={tryUnlock}>
                  <input
                    type="password"
                    className="form-control mb-3"
                    placeholder={t('developers.gate.placeholder')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    autoFocus
                  />
                  {error && (
                    <div className="text-danger small mb-3">
                      {t('developers.gate.error')}
                    </div>
                  )}
                  <button type="submit" className="btn btn-primary w-100" disabled={checking}>
                    <i className="bi bi-unlock me-2"></i>
                    {t('developers.gate.submit')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section">
      <div className="container">
        <SectionHeader
          title={t('developers.content.heading')}
          subtitle={t('developers.content.subheading')}
        />
        <div className="row g-4">
          {upcomingProducts.map((p) => (
            <div key={p.id} className="col-md-6 col-lg-4">
              <div className="feature-box">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="feature-icon">
                    <i className={`bi bi-${p.icon}`}></i>
                  </div>
                  <span className={`status-badge status-${p.status === 'inProgress' ? 'in-progress' : p.status}`}>
                    {t(`developers.status.${p.status}`)}
                  </span>
                </div>
                <h3>{p.name}</h3>
                <p className="description">
                  {lang === 'no' ? p.descriptionNo : p.descriptionEn}
                </p>
                <div className="feature-cta">
                  <a
                    href={`mailto:${company.email}?subject=StructuraMatrix ${p.name}`}
                    className="btn btn-outline-primary btn-sm"
                  >
                    <i className="bi bi-hand-thumbs-up me-2"></i>
                    {t('developers.content.interest')}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
